import * as React from 'react';

import { Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, OutlinedInput, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { DataGrid, GridColDef, GridRowParams, GridRenderCellParams } from '@mui/x-data-grid';
import { Interface, InterfaceStatusStrings, enumerateInterfaces } from '../../types/Interface';
import { copyArrayWithIndexChanged } from '../../Utils';
import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { TestCombination, TestComboSupportVal, getTestComboSupport, updateTestComboSupport, deleteTestComboSupport } from '../../types/TestCombinations';

import { httpPost } from "../../Web";
import { ApiResponse } from "../../types/ApiResponse";

const columns: GridColDef[] = [{
        field: 'isValid',
        headerName: 'Valid Combination',
        flex: 1,
        renderCell: (params: GridRenderCellParams<any, boolean>) => {
            if (params.row.isValid === false) {
                return <CloseIcon color={'error'} />
            }
            if (params.row.isValid === true) {
                return <CheckIcon color={'success'} />
            }
            return <RemoveCircleOutlineIcon />
        },
    },
    {
        field: 'name',
        headerName: 'Name',
        flex: 3
    },
    {
        field: 'shortName',
        headerName: 'ShortName',
        flex: 1,
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        valueFormatter: (value?: number) => {
            if (value == null) {
                return '';
            }
            return InterfaceStatusStrings[value];
        }
    },
    {
        field: 'id',
        headerName: 'ID',
        flex: 1
    }
];

export const GetEmptyInterface = (): Interface => ({
    name: '',
    shortName: '',
    status: 0,
    guid: '',
    id: 0
});

interface RowValue extends Interface {
    isValid?: boolean; // This is used to indicate if the interface is valid for the selected test combination
};

const getRowValues = (ifaces: Interface[], allSupportedCombo: TestComboSupportVal[]): RowValue[] => {
    if (ifaces.length === 0) {
        return [];
    }
    let rowvalues: RowValue[] = [];
    ifaces.forEach(iface => {
        const isValid = allSupportedCombo.some(combo => combo.interfaceId === iface.id && combo.testCombinationId !== undefined);
        let value: RowValue = { ...iface, isValid: isValid };
        rowvalues.push(value);
    });
    return rowvalues;
}

const updateInterface = (data: Interface, abortController: AbortController): Promise<ApiResponse<Interface>> => httpPost(`/api/interface/update`, data, abortController);

export const InterfaceAdmin = ({ selectedTestCombination, selectedInterface, setSelectedInterface }:
    { selectedTestCombination: TestCombination, selectedInterface: Interface, setSelectedInterface: (value: React.SetStateAction<Interface>) => void  }) => {
    const { showSnackbar } = useSnackbar();
    const [allinterfaces, setAllinterfaces] = React.useState<Interface[]>([]);
    const [rowvalues, setRowvalues] = React.useState<RowValue[]>([]);
    const [allSupportedCombo, setAllSupportedCombo] = React.useState<TestComboSupportVal[]>([]);
    const [selectedCombo, setselectedCombo] = React.useState<TestComboSupportVal>({});
    const [alteredCombo, setAlteredCombo] = React.useState<TestComboSupportVal>({});

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchTestCombinations = async () => {
            const allinterfacesResponse = await enumerateInterfaces(new AbortController());
            if (allinterfacesResponse.failed) {
                showUserFeedback(allinterfacesResponse.errorText);
                return;
            }
            setAllinterfaces(allinterfacesResponse.result);
            setRowvalues(getRowValues(allinterfacesResponse.result, []));
        }
        fetchTestCombinations();
    }, [showUserFeedback]);

    React.useEffect(() => {
        const fetchSupportedCombinations = async () => {
            if (!selectedTestCombination || selectedTestCombination.id === undefined
                || selectedTestCombination.id === 0) {
                setAllSupportedCombo([]);
                setRowvalues(getRowValues(allinterfaces, []));
                return;
            }

            const testComboSupport: TestComboSupportVal = {
                testCombinationId: selectedTestCombination.id,
                interfaceId: undefined, // undefined means all interfaces 
                testCaseId: 0, // 0 means not applicable
                interfacePropertyId: 0 // 0 means not applicable
            }
            const allcombosResponse = await getTestComboSupport(testComboSupport, new AbortController());
            if (allcombosResponse.failed) {
                showUserFeedback(allcombosResponse.errorText);
                return;
            }
            setAllSupportedCombo(allcombosResponse.result);
            setRowvalues(getRowValues(allinterfaces, allcombosResponse.result));
            setSelectedInterface(GetEmptyInterface());
            setselectedCombo({});
            setAlteredCombo({});
        }
        fetchSupportedCombinations();
    }, [showUserFeedback, setSelectedInterface, selectedTestCombination, allinterfaces]);

    const saveHandler = async (interfaceData: Interface) => {
        const updatedInterface = await updateInterface(interfaceData, new AbortController());
        if (updatedInterface.failed) {
            showUserFeedback(updatedInterface.errorText);
            return;
        }
        const updatedInterfaceLoc = GetEmptyInterface();
        setSelectedInterface(updatedInterfaceLoc);
        let allinterfacesLoc = copyArrayWithIndexChanged(allinterfaces, allinterfaces.findIndex(tc => tc.id === updatedInterfaceLoc.id), updatedInterfaceLoc);
        setAllinterfaces(allinterfacesLoc);

        if (selectedTestCombination.id !== undefined && selectedTestCombination.id > 0) {
            if (selectedCombo.interfaceId !== alteredCombo.interfaceId) {
                if (alteredCombo.interfaceId) {
                    updateTestComboSupport(alteredCombo, new AbortController()).then((response) => {
                        if (response.failed) {
                            showUserFeedback(response.errorText);
                            return;
                        }
                        const updatedCombo = response.result;
                        const allSupportedComboLoc = copyArrayWithIndexChanged(allSupportedCombo, allSupportedCombo.findIndex(combo => combo.id === updatedCombo.id), updatedCombo);
                        setAllSupportedCombo(allSupportedComboLoc);
                    });
                }
                else {
                    deleteTestComboSupport(selectedCombo.id ?? 0, new AbortController()).then((response) => {
                        if (response.failed) {
                            showUserFeedback(response.errorText);
                            return;
                        }
                        const updatedCombo = response.result;
                        const allSupportedComboLoc = allSupportedCombo.filter(combo => combo.id !== updatedCombo.id);
                        setAllSupportedCombo(allSupportedComboLoc);
                    });
                }
                setselectedCombo({});
                setAlteredCombo({});
            }
        }
    }

    const onSubmitHandler = (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        saveHandler(selectedInterface);
    }

    const onSelectionChange = (sel: GridRowParams) => {
        if (sel.id === selectedInterface.id) {
            setSelectedInterface(GetEmptyInterface()); // deselect if already selected
            setselectedCombo({});
            setAlteredCombo({});
            return;
        }
        setSelectedInterface(allinterfaces.find(iface => iface.id === sel.id) as Interface);
        const selCombo: TestComboSupportVal = allSupportedCombo.find(combo => combo.interfaceId === sel.id) ?? {};
        setselectedCombo(selCombo);
        setAlteredCombo(selCombo);
    }

    return <>
        <Typography variant='h4'>{selectedTestCombination.id ? 'Configure Interfaces for ' + selectedTestCombination.combinationName
            : 'Interface Settings. Please select a Test Combination to configure its valid Interfaces.'}</Typography>
        <Box sx={{ height: '80%', width: '99%', mt: '20px' }}>
            <Grid container spacing={2} alignItems={'center'} >
                <Grid size={9} paddingBottom={10}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='shortName'>{"Short Name"}</InputLabel>
                        <OutlinedInput notched name='shortName' id='shortName'
                            label={"Short Name"} value={selectedInterface?.shortName}
                            onChange={(e) => setSelectedInterface({ ...selectedInterface, shortName: e.target.value })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={1} />
                <Grid size={2} paddingBottom={10}>
                    <FormControlLabel control={<Checkbox
                        checked={alteredCombo.interfaceId !== undefined ? true : false}
                        inputProps={{ 'aria-label': 'controlled' }}
                        onChange={(e) => setAlteredCombo(e.target.checked ? { interfaceId: selectedInterface.id, testCombinationId: selectedTestCombination.id } : {})} />}
                        label="Valid"
                    />
                </Grid>
                <Grid size={12} paddingBottom={10}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='name'>{"Name"}</InputLabel>
                        <OutlinedInput notched name='name' id='name'
                            label={"Name"} value={selectedInterface?.name}
                            onChange={(e) => setSelectedInterface({ ...selectedInterface, name: e.target.value })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl required fullWidth>
                        <Autocomplete id='ifaceStatus' options={InterfaceStatusStrings}
                            value={InterfaceStatusStrings[(selectedInterface?.status) ?? 0]}
                            onChange={(e, value) => setSelectedInterface({ ...selectedInterface, status: InterfaceStatusStrings.indexOf(value as string) })}
                            renderInput={(params) =>
                                <TextField name='ifaceStatus' multiline
                                    {...params}
                                    label={"Status"}
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                            borderColor: 'divider',
                                        },
                                    }}
                                />
                            }
                        />
                    </FormControl>
                </Grid>
                <Grid size={1} />
                <Grid size={2}>
                    <Button fullWidth startIcon={<ClearIcon />}
                        onClick={() => {
                            setSelectedInterface(GetEmptyInterface());
                        }}>
                        New
                    </Button>
                </Grid>
                <Grid size={1} />
                <Grid size={2} marginRight='0px' marginLeft='auto'>
                    <Button fullWidth startIcon={<SaveIcon />}
                        onClick={(e) => {
                            onSubmitHandler(e);
                        }}>
                        Save
                    </Button>
                </Grid>
            </Grid>
            <Box sx={{ width: '100%' }} marginTop={15}>
                <DataGrid
                    columns={columns}
                    rows={rowvalues}
                    columnVisibilityModel={{ id: false }}
                    onRowClick={onSelectionChange}
                    getRowClassName={(params) => params.row.id === selectedInterface.id ? 'selectRowClass' : ''}
                    hideFooter
                    sx={{
                        "& .selectRowClass": {
                            color: 'primary.main'
                        },
                    }}
                />
            </Box>
        </Box>
    </>
}
